import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import MCard from "components/Shared/MCard";
import { CaseFollowUpDto, CaseFollowUpInsertRequest, CaseFollowUpUpdateRequest } from "interfaces/Mueller.Business.Models";
import { getCaseFollowUp, insertCaseFollowUp, updateCaseFollowUp, completeCaseFollowUp } from "requests/caseFollowUp";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DATE_TIME, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import { createRequestCoalescer } from "tools/requestCoalescer";

const caseFollowUpCoalescer = createRequestCoalescer<CaseFollowUpDto, number>();
const activeRequest = { current: true };

interface CaseFollowUpProps {
  controlNumber: number;
  internetUserId: number;
}

const CaseFollowUp = ({
  controlNumber,
  internetUserId,
}: CaseFollowUpProps) => {

  const mounted = useRef(true);
  const caseFollowUpRequestCleanup = useRef<() => void>(() => {});
  const [caseFollowUp, setCaseFollowUp] = useState<CaseFollowUpDto>(null);
  const [fetchingCaseFollowUp, setFetchingCaseFollowUp] = useState(false);
  const [updatingCaseFollowUp, setUpdatingCaseFollowUp] = useState(false);

  useEffect(() => {
    return () => {
      mounted.current = false;
      caseFollowUpRequestCleanup.current();
    };
  }, []);

  useEffect(() => {
    setCaseFollowUp(null);
    fetchCaseFollowUp();
  }, [controlNumber]);

  const fetchCaseFollowUp = (forceRefresh = false) => {
    if (!controlNumber) return;

    setFetchingCaseFollowUp(true);
    const runRequest = forceRefresh ? caseFollowUpCoalescer.refresh : caseFollowUpCoalescer.run;

    caseFollowUpRequestCleanup.current();
    caseFollowUpRequestCleanup.current = runRequest(
      controlNumber,
      (resolve, reject) => getCaseFollowUp(controlNumber, resolve, activeRequest, reject),
      (response) => {
        if (mounted.current) {
          setCaseFollowUp(response);
          setFetchingCaseFollowUp(false);
        }
      },
      () => {
        if (mounted.current) {
          setFetchingCaseFollowUp(false);
        }
      },
    );
  };

  const getFields = () => {
    let fields = [];
    fields.push(
      {
        key: "followUpDate",
        label: "Follow-up Date",
        dataType: DATA_TYPE_DATE_TIME,
        value: caseFollowUp?.followUpDate,
        editable: true,
      },
      {
        key: "note",
        label: "Note",
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        value: caseFollowUp?.note,
        editable: true,
      }
    );
    if (caseFollowUp) {
      fields.push(
        {
          key: "markCompleted",
          label: "Mark Completed",
          dataType: DATA_TYPE_BOOLEAN,
          value: false,
          editable: true,
        }
      );
    }
    return fields;
  };

  const saveChanges = (editFields) => {
    if (!editFields.length) return;
    
    setUpdatingCaseFollowUp(true);

    const updatedFollowUpDate = editFields.filter((f) => f.key === "followUpDate")[0]?.value;
    const markCompleted = editFields.filter((f) => f.key === "markCompleted")[0]?.value;
    const updatedNote = editFields.filter((f) => f.key === "note")[0]?.value;
    
    if (!caseFollowUp && updatedFollowUpDate) {
      const insertRequest: CaseFollowUpInsertRequest = {
        internetUserId: internetUserId,
        controlIdNumber: controlNumber,
        followUpDate: updatedFollowUpDate,
        note: updatedNote,
      };
      insertCaseFollowUp(insertRequest, () => {
        setUpdatingCaseFollowUp(false);
        fetchCaseFollowUp(true);
      }, mounted);
    } else if (caseFollowUp && markCompleted) {
      completeCaseFollowUp(caseFollowUp.id, () => {
        setUpdatingCaseFollowUp(false);
        fetchCaseFollowUp(true);
      }, mounted);
    } else if (caseFollowUp && (updatedFollowUpDate || updatedNote)) {
      const updateRequest: CaseFollowUpUpdateRequest = {
        id: caseFollowUp.id,
        followUpDate: updatedFollowUpDate ?? caseFollowUp.followUpDate,
        note: updatedNote ?? caseFollowUp.note,
      };
      updateCaseFollowUp(caseFollowUp.id, updateRequest, () => {
        setUpdatingCaseFollowUp(false);
        fetchCaseFollowUp(true);
      }, mounted);
    } else {
      setUpdatingCaseFollowUp(false);
    }
  };
  
  if (fetchingCaseFollowUp || updatingCaseFollowUp) return (<MSpinner />);

  return (<MCard
    title="Case Follow Up"
    fields={getFields()}
    isEditable={true}
    onEditFinished={saveChanges}
  />);
};

const mapStateToProps = state => ({
  internetUserId: state.user.profile.internetUserId,
});

export default connect(mapStateToProps)(CaseFollowUp);